import { DOCUMENT_FORM_NAME_BASE_ACRES, DOCUMENT_FORM_NAME_FARMS } from "@datanac/datanac-api-toolkit";
import { ApiHelper, UsersApiHelper } from "api/ApiHelper";
import { DOCUMENT_FORM_NAME_FARMSFSA578FORM, TEMPLATE_URL_PATH } from "components/Financial/FinancialHelpers";
import ExcelJS from "exceljs";
import fileDownload from "js-file-download";

import { reverseBaseAcresImportTransformsForExport } from "../../baseAcresExportReverseTransforms";
import {
  SHARE_PERCENT_KEYS as BASE_ACRES_SHARE_PERCENT_KEYS,
  SHEET_NAME_EXPECTED as BASE_ACRES_SHEET_NAME_EXPECTED,
  TEMPLATE_FILE_NAME as BASE_ACRES_TEMPLATE_FILE_NAME,
} from "../../exportBaseAcresWorkbook";
import {
  SHARE_PERCENT_KEYS as FARMS578_SHARE_PERCENT_KEYS,
  SHEET_NAME_EXPECTED as FARMS578_SHEET_NAME_EXPECTED,
  TEMPLATE_FILE_NAME as FARMS578_TEMPLATE_FILE_NAME,
  SHARE_PERCENT_NUM_FMT,
} from "../../exportFarms578Workbook";
import { reverseFarms578ImportTransformsForExport } from "../../farms578Codes";
import { coalesceBaseAcresExportSharePercent } from "../../helpers";

/// Document Chat Assistant — types that expose Export (keep aligned with handlers below).
export const CHAT_ASSISTANT_EXPORTABLE_DOCUMENT_FORM_NAMES = Object.freeze([
  DOCUMENT_FORM_NAME_FARMSFSA578FORM,
  DOCUMENT_FORM_NAME_BASE_ACRES,
]);

export const isChatAssistantExportSupported = (documentType) =>
  !!documentType && CHAT_ASSISTANT_EXPORTABLE_DOCUMENT_FORM_NAMES.includes(documentType);

/**
 * Defaults for Farms 578 export from the document assistant (all overridable via `exportOptions.farms578`).
 * @property {'jsonRows'|'producerCrops'|'merged'} rowSource jsonRows: only structured JSON arrays; producerCrops: DB crops only (like legacy workbook export); merged: DB rows overlaid by matching JSON rows, then unmatched JSON appended.
 * @property {string[]} mergeKeys Fields to match JSON rows to producer crops when rowSource is 'merged' (first equality on any key wins).
 * @property {boolean} appendUnmatchedJsonRows When merged, append JSON rows that did not match any API crop.
 * @property {'fail'|'useProducerCrops'} whenNoJsonRows When rowSource is jsonRows but the document has no row array: `'fail'` (empty workbook) vs `'useProducerCrops'`.
 */
export const FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS = Object.freeze({
  rowSource: "jsonRows",
  mergeKeys: Object.freeze(["farm_field_crop_token", "FarmFieldCropToken", "id", "Id"]),
  appendUnmatchedJsonRows: true,
  whenNoJsonRows: "useProducerCrops",
  /** When true (merged / producerCrops paths), fills commodity_code from FSA xref like the workbook export */
  enrichCommodityCodesFromXref: true,
});

export const BASE_ACRES_ASSISTANT_EXPORT_DEFAULT_OPTIONS = Object.freeze({
  rowSource: "jsonRows",
  mergeKeys: Object.freeze(["farm_field_crop_token", "FarmFieldCropToken", "id", "Id"]),
  appendUnmatchedJsonRows: true,
  whenNoJsonRows: "useProducerCrops",
});

const snakeToCamel = (headerKey) => headerKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

function pickField(row, headerKey) {
  if (row === null || row === undefined || headerKey === null || headerKey === undefined) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, headerKey)) return row[headerKey];
  const camel = snakeToCamel(headerKey);
  if (Object.prototype.hasOwnProperty.call(row, camel)) return row[camel];
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  if (Object.prototype.hasOwnProperty.call(row, pascal)) return row[pascal];
  return undefined;
}

function normMatchValue(v) {
  if (v === null || v === undefined) return "";
  return String(v).trim().toLowerCase();
}

/** ProducerFarmFieldCrops-shaped arrays from structured file JSON, plus generic fallback. */
export function extractFarms578RowObjects(structuredContent) {
  if (!structuredContent || typeof structuredContent !== "object") {
    return [];
  }
  const preferredKeys = ["ProducerFarmFieldCrops", "producerFarmFieldCrops", "producer_farm_field_crops"];
  for (const k of preferredKeys) {
    const v = structuredContent[k];
    if (Array.isArray(v) && v.length > 0) {
      return v;
    }
  }
  for (const key of Object.keys(structuredContent)) {
    if (key === "producer_token" || key === "agent_token") continue;
    const value = structuredContent[key];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }
  return [];
}

/** ProducerFarmFieldCrops-shaped arrays from structured file JSON, plus generic fallback. */
export function extractBaseAcresRowObjects(structuredContent) {
  if (!structuredContent || typeof structuredContent !== "object") {
    return [];
  }
  const preferredKeys = ["ProducerFarmFieldCrops", "producerFarmFieldCrops", "producer_farm_field_crops"];
  for (const k of preferredKeys) {
    const v = structuredContent[k];
    if (Array.isArray(v) && v.length > 0) {
      return v;
    }
  }
  for (const key of Object.keys(structuredContent)) {
    if (key === "producer_token" || key === "agent_token") continue;
    const value = structuredContent[key];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }
  return [];
}

function buildRowDataForTemplate(colMap, cropLike, crop_year, options) {
  const { preferDocumentCropYear = true, sharePercentKeys = [] } = options;
  const rowData = {};
  Object.keys(colMap).forEach((key) => {
    if (key === "crop_year") {
      const fromDoc = pickField(cropLike, "crop_year") ?? pickField(cropLike, "cropYear");
      if (preferDocumentCropYear && fromDoc !== null && fromDoc !== undefined && fromDoc !== "") {
        rowData[key] = fromDoc;
      } else {
        rowData[key] = crop_year;
      }
      return;
    }
    const raw = pickField(cropLike, key);
    if (sharePercentKeys.includes(key)) {
      const v = raw;
      rowData[key] = v !== null && v !== undefined && v !== "" ? Math.round(Number(v) * 10000) / 10000 : null;
    } else {
      rowData[key] = raw !== undefined ? raw : null;
    }
  });
  return rowData;
}

function writeRowsToSheet(sheet, colMap, rowObjects, crop_year, buildOptions) {
  const { sharePercentKeys = [] } = buildOptions || {};
  rowObjects.forEach((cropLike) => {
    const rowData = buildRowDataForTemplate(colMap, cropLike, crop_year, buildOptions);
    const newRow = sheet.addRow([]);
    Object.entries(colMap).forEach(([key, colNum]) => {
      const cell = newRow.getCell(colNum);
      cell.value = rowData[key] ?? null;
      if (sharePercentKeys.includes(key) && cell.value !== null && cell.value !== "") {
        cell.numFmt = SHARE_PERCENT_NUM_FMT;
      }
    });
  });
}

async function loadTemplateSheet(globalState, { templateFileName, sheetNameExpected }) {
  const urlResponse = await UsersApiHelper.getPublicDocument(
    { document_path: TEMPLATE_URL_PATH, document_name: templateFileName },
    globalState
  );

  const templateUrl = urlResponse?.url;
  if (!templateUrl) {
    throw new Error("Could not retrieve template URL.");
  }

  const templateResponse = await fetch(templateUrl);
  if (!templateResponse.ok) {
    throw new Error(`Template download failed: ${templateResponse.status}`);
  }
  const arrayBuffer = await templateResponse.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    throw new Error("Template workbook has no worksheets.");
  }
  if (sheet.name !== sheetNameExpected) {
    console.warn(`Expected sheet name "${sheetNameExpected}", got "${sheet.name}"`);
  }

  const headerRow = sheet.getRow(1);
  const colMap = {};
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const key = cell.value;
    if (key) colMap[key] = colNumber;
  });

  const lastRow = sheet.rowCount;
  for (let r = lastRow; r >= 3; r--) {
    sheet.spliceRows(r, 1);
  }

  return { workbook, sheet, colMap };
}

function rowsMatchForMerge(apiRow, jsonRow, mergeKeys) {
  return mergeKeys.some((k) => {
    const av = pickField(apiRow, k) ?? apiRow?.[k];
    const jv = pickField(jsonRow, k) ?? jsonRow?.[k];
    if (av === null || av === undefined || jv === null || jv === undefined) return false;
    return normMatchValue(av) === normMatchValue(jv);
  });
}

/**
 * Build the list of row objects written to the XLSX (after merge / enrichment).
 * Does not mutate inputs.
 */
export async function resolveFarms578ExportRows(globalState, crop_year, structuredContent, farms578Options = {}) {
  const opt = {
    ...FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS,
    ...farms578Options,
    mergeKeys: farms578Options.mergeKeys ?? [...FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS.mergeKeys],
  };

  const jsonRows = extractFarms578RowObjects(structuredContent);

  let commodityXrefs = [];
  if (opt.rowSource !== "jsonRows" || opt.enrichCommodityCodesFromXref) {
    commodityXrefs = (await ApiHelper.selectObject("FSA.ARCPLC", "FSACommodities", {})) ?? [];
  }

  if (opt.rowSource === "jsonRows") {
    if (jsonRows.length === 0) {
      if (opt.whenNoJsonRows === "useProducerCrops") {
        const crops =
          (await UsersApiHelper.selectProducerFarmFieldCrops(
            { producer_token: globalState.producer_token, crop_year, is_active: true },
            globalState
          )) ?? [];
        return crops.map((c) =>
          opt.enrichCommodityCodesFromXref ? reverseFarms578ImportTransformsForExport(c, commodityXrefs) : c
        );
      }
      return [];
    }
    return jsonRows.map((r) =>
      opt.enrichCommodityCodesFromXref ? reverseFarms578ImportTransformsForExport(r, commodityXrefs) : r
    );
  }

  if (opt.rowSource === "producerCrops") {
    const crops =
      (await UsersApiHelper.selectProducerFarmFieldCrops(
        { producer_token: globalState.producer_token, crop_year, is_active: true },
        globalState
      )) ?? [];
    return crops.map((c) =>
      opt.enrichCommodityCodesFromXref ? reverseFarms578ImportTransformsForExport(c, commodityXrefs) : c
    );
  }

  /** merged */
  const apiCrops =
    (await UsersApiHelper.selectProducerFarmFieldCrops(
      { producer_token: globalState.producer_token, crop_year, is_active: true },
      globalState
    )) ?? [];

  const enrichedApi = apiCrops.map((c) =>
    opt.enrichCommodityCodesFromXref ? reverseFarms578ImportTransformsForExport(c, commodityXrefs) : { ...c }
  );

  const usedJson = new Set();
  const merged = enrichedApi.map((apiRow) => {
    const jIdx = jsonRows.findIndex((jr, i) => !usedJson.has(i) && rowsMatchForMerge(apiRow, jr, opt.mergeKeys));
    if (jIdx >= 0) {
      usedJson.add(jIdx);
      return { ...apiRow, ...jsonRows[jIdx] };
    }
    return apiRow;
  });

  if (opt.appendUnmatchedJsonRows) {
    jsonRows.forEach((jr, i) => {
      if (!usedJson.has(i)) {
        const row = opt.enrichCommodityCodesFromXref
          ? reverseFarms578ImportTransformsForExport(jr, commodityXrefs)
          : jr;
        merged.push(row);
      }
    });
  }

  return merged;
}

export async function resolveBaseAcresExportRows(globalState, crop_year, structuredContent, baseAcresOptions = {}) {
  const opt = {
    ...BASE_ACRES_ASSISTANT_EXPORT_DEFAULT_OPTIONS,
    ...baseAcresOptions,
    mergeKeys: baseAcresOptions.mergeKeys ?? [...BASE_ACRES_ASSISTANT_EXPORT_DEFAULT_OPTIONS.mergeKeys],
  };

  const jsonRows = extractBaseAcresRowObjects(structuredContent);

  if (opt.rowSource === "jsonRows") {
    if (jsonRows.length === 0) {
      if (opt.whenNoJsonRows === "useProducerCrops") {
        return (
          (await UsersApiHelper.selectProducerFarmFieldCrops(
            { producer_token: globalState.producer_token, crop_year, is_active: true },
            globalState
          )) ?? []
        );
      }
      return [];
    }
    return jsonRows;
  }

  if (opt.rowSource === "producerCrops") {
    return (
      (await UsersApiHelper.selectProducerFarmFieldCrops(
        { producer_token: globalState.producer_token, crop_year, is_active: true },
        globalState
      )) ?? []
    );
  }

  const apiRows =
    (await UsersApiHelper.selectProducerFarmFieldCrops(
      { producer_token: globalState.producer_token, crop_year, is_active: true },
      globalState
    )) ?? [];
  const usedJson = new Set();
  const merged = apiRows.map((apiRow) => {
    const jIdx = jsonRows.findIndex((jr, i) => !usedJson.has(i) && rowsMatchForMerge(apiRow, jr, opt.mergeKeys));
    if (jIdx >= 0) {
      usedJson.add(jIdx);
      return { ...apiRow, ...jsonRows[jIdx] };
    }
    return apiRow;
  });
  if (opt.appendUnmatchedJsonRows) {
    jsonRows.forEach((jr, i) => {
      if (!usedJson.has(i)) merged.push(jr);
    });
  }
  return merged;
}

/**
 * Streams a Farms 578 workbook using structured assistant JSON (and optional producer-crop merge).
 * @param {object} params
 * @param {object} params.globalState
 * @param {string|number} params.crop_year
 * @param {object|null} [params.structuredContent] Root `documentStructuredJson.content`
 * @param {object} [params.farms578Options] Overrides for `FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS`
 */
export async function exportFarms578WorkbookFromAssistant({
  globalState,
  crop_year,
  structuredContent = null,
  farms578Options = {},
}) {
  const opt = {
    ...FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS,
    ...farms578Options,
    mergeKeys: farms578Options.mergeKeys ?? [...FARMS578_ASSISTANT_EXPORT_DEFAULT_OPTIONS.mergeKeys],
  };

  const rows = await resolveFarms578ExportRows(globalState, crop_year, structuredContent, opt);

  if (!rows?.length) {
    const msg =
      opt.rowSource === "jsonRows" && opt.whenNoJsonRows !== "useProducerCrops"
        ? "No rows in document content to export."
        : "No crops found for the selected year — nothing to export.";
    return { success: false, message: msg, variant: "warning" };
  }

  const { workbook, sheet, colMap } = await loadTemplateSheet(globalState, {
    templateFileName: FARMS578_TEMPLATE_FILE_NAME,
    sheetNameExpected: FARMS578_SHEET_NAME_EXPECTED,
  });
  writeRowsToSheet(sheet, colMap, rows, crop_year, {
    preferDocumentCropYear: true,
    sharePercentKeys: FARMS578_SHARE_PERCENT_KEYS,
  });

  const buffer = await workbook.xlsx.writeBuffer();
  fileDownload(buffer, `Farms-578-Export-${crop_year}.xlsx`);
  return { success: true };
}

export async function exportBaseAcresWorkbookFromAssistant({
  globalState,
  crop_year,
  structuredContent = null,
  baseAcresOptions = {},
}) {
  const opt = {
    ...BASE_ACRES_ASSISTANT_EXPORT_DEFAULT_OPTIONS,
    ...baseAcresOptions,
    mergeKeys: baseAcresOptions.mergeKeys ?? [...BASE_ACRES_ASSISTANT_EXPORT_DEFAULT_OPTIONS.mergeKeys],
  };

  const rowsRaw = await resolveBaseAcresExportRows(globalState, crop_year, structuredContent, opt);

  if (!rowsRaw?.length) {
    const msg =
      opt.rowSource === "jsonRows" && opt.whenNoJsonRows !== "useProducerCrops"
        ? "No rows in document content to export."
        : "No base acres found for the selected year — nothing to export.";
    return { success: false, message: msg, variant: "warning" };
  }

  const rows = coalesceBaseAcresExportSharePercent(rowsRaw.map((r) => ({ ...r })));

  const { workbook, sheet, colMap } = await loadTemplateSheet(globalState, {
    templateFileName: BASE_ACRES_TEMPLATE_FILE_NAME,
    sheetNameExpected: BASE_ACRES_SHEET_NAME_EXPECTED,
  });
  const sheetRows = rows.map((r) => reverseBaseAcresImportTransformsForExport(r));
  writeRowsToSheet(sheet, colMap, sheetRows, crop_year, {
    preferDocumentCropYear: false,
    sharePercentKeys: BASE_ACRES_SHARE_PERCENT_KEYS,
  });

  const buffer = await workbook.xlsx.writeBuffer();
  fileDownload(buffer, `Base-Acres-Export-${crop_year}.xlsx`);
  return { success: true };
}

/**
 * @param {object} [extras]
 * @param {object|null} [extras.structuredContent] Document Contents root (`documentStructuredJson.content`).
 * @param {object} [extras.exportOptions] Per–document-type options, e.g. `{ farms578: { rowSource: 'merged' } }`.
 */
export const runChatAssistantExport = async (documentType, globalState, crop_year, enqueueSnackbar, extras = {}) => {
  const { structuredContent = null, exportOptions = {} } = extras;
  const farms578Opts = exportOptions.farms578 ?? {};
  const baseAcresOpts = exportOptions.baseAcres ?? {};

  switch (documentType) {
    case DOCUMENT_FORM_NAME_FARMSFSA578FORM:
    case DOCUMENT_FORM_NAME_FARMS:
      return exportFarms578WorkbookFromAssistant({
        globalState,
        crop_year,
        structuredContent,
        farms578Options: farms578Opts,
      });
    case DOCUMENT_FORM_NAME_BASE_ACRES:
      return exportBaseAcresWorkbookFromAssistant({
        globalState,
        crop_year,
        structuredContent,
        baseAcresOptions: baseAcresOpts,
      });
    default:
      return { success: false, message: `Export not supported for ${documentType}` };
  }
};
